import React from 'react'

import styles from './ConfirmDialog.module.scss'

import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  /** Renders above the action buttons. Strings are wrapped in a paragraph; nodes render verbatim. */
  message?: string | React.ReactNode
  /** Extra emphasized/warning block rendered below the message (e.g. "This cannot be undone"). */
  warning?: string | React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Controls the confirm button style. `destructive` for irreversible actions. */
  confirmType?: 'dark' | 'destructive'
  isConfirming?: boolean
  /** Disables the confirm button without putting it in the loading state. Use for client-side validation gates. */
  isConfirmDisabled?: boolean
  /** Omits the cancel button for acknowledge-only dialogs. */
  hideCancel?: boolean
  /** Forwarded to DialogWrapper for cases where the body needs more horizontal room than the default. */
  minWidth?: number | string
  onConfirm: () => void
  onCancel: () => void
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  warning,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmType = 'destructive',
  isConfirming = false,
  isConfirmDisabled = false,
  hideCancel = false,
  minWidth,
  onConfirm,
  onCancel,
}) => {
  // Escape and overlay click both route through close, so they must respect the same
  // in-flight guard as the Cancel button — otherwise the dialog dismisses mid-save.
  const close = () => {
    if (isConfirming) return
    onCancel()
  }

  return (
    <DialogWrapper isOpen={isOpen} title={title} close={close} minWidth={minWidth}>
      <div className={styles.content}>
        {typeof message === 'string' ? <p className={styles.message}>{message}</p> : message}
        {warning && <div className={styles.warning}>{warning}</div>}
      </div>
      <div className={styles.actions}>
        {!hideCancel && <Button title={cancelLabel} onClick={onCancel} type="light" isDisabled={isConfirming} />}
        <Button
          title={confirmLabel}
          onClick={onConfirm}
          type={confirmType}
          isLoading={isConfirming}
          isDisabled={isConfirming || isConfirmDisabled}
        />
      </div>
    </DialogWrapper>
  )
}

export default ConfirmDialog
